/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import BrowserExtensionIcon from '@atlaskit/icon-lab/core/browser-extension';
import TerminalIcon from '@atlaskit/icon-lab/core/terminal';
import AiChatIcon from '@atlaskit/icon/core/ai-chat';
import DebugIcon from '@atlaskit/icon/core/bug';
import DeleteIcon from '@atlaskit/icon/core/delete';
import EditIcon from '@atlaskit/icon/core/edit';
import FeedbackIcon from '@atlaskit/icon/core/feedback';
import QuestionCircleIcon from '@atlaskit/icon/core/question-circle';
import ScreenIcon from '@atlaskit/icon/core/screen';
import SettingsIcon from '@atlaskit/icon/core/settings';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import UploadIcon from '@atlaskit/icon/core/upload';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { useStoreUserMessagesPreference } from '@atlassian/conversation-assistant-store/controllers/store-user-messages-be';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';

import { chatHeaderTestIds } from '../../common/constants/constants';
import { ChatHeaderMenu } from '../../common/ui/chat-header-menu';
import { ChatHeaderMenuList } from '../../common/ui/chat-header-menu/chat-header-menu-list';
import { CheckboxMenuItem } from '../../common/ui/chat-header-menu/checkbox-menu-item';
import { MenuItem } from '../../common/ui/chat-header-menu/menu-item';
import { SelectorMenuItem } from '../../common/ui/chat-header-menu/selector-menu-item';

import { ChatHeaderMenuSlidingPanel, LIST_WIDTH_PX } from './chat-header-menu-sliding-panel';
import {
	chatHeaderMessages as messages,
	getShareButtonLabel,
	type ShareConversationDisabledReason,
} from './messages';
import {
	type MoreMenuSubMenuItem,
	type MoreMenuSubMenuKey,
	RovoMobileQrSubMenuItems,
} from './rovo-mobile-qr-sub-menu-items';
import { WhatsNewMenuItem } from './whats-new-menu-item';

const styles = cssMap({
	moreMenuPostOfficeSpotlightWrapper: {
		position: 'relative',
	},
});

type OpenSubMenuKey = 'list' | MoreMenuSubMenuKey;

interface ChatHeaderMoreMenuProps {
	onFeedbackClick?: () => void;
	onDebugClick?: () => void;
	onConversationDelete?: () => void;
	onConversationRename?: () => void;
	onDesktopAppClick?: () => void;
	onSlackAppClick?: () => void;
	onTeamsAppClick?: () => void;
	onChromeExtensionClick?: () => void;
	onCliClick?: () => void;
	onSettingsClick?: () => void;
	onShareClick?: () => void;
	isShareDisabled?: ShareConversationDisabledReason;
	sitePickerRender?: JSX.Element;
	subMenuItems?: MoreMenuSubMenuItem[];
	onMenuOpen?: () => void;
	assistanceServiceParams?: AssistanceServiceInputParam['assistanceServiceParams'];
}

export const ChatHeaderMoreMenu = (props: ChatHeaderMoreMenuProps): React.JSX.Element | null => {
	di(useUserPreferences, useStoreUserMessagesPreference, fg);

	if (props.assistanceServiceParams && fg('ai_mate_store_messages_be_driven')) {
		return (
			<ChatHeaderMoreMenuWithBackendPreference
				{...props}
				assistanceServiceParams={props.assistanceServiceParams}
			/>
		);
	}

	return <ChatHeaderMoreMenuWithLocalPreference {...props} />;
};

const ChatHeaderMoreMenuWithBackendPreference = ({
	assistanceServiceParams,
	...props
}: ChatHeaderMoreMenuProps & {
	assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'];
}): React.JSX.Element | null => {
	di(useStoreUserMessagesPreference);
	const { storeUserMessages, storeUserMessagesEligible, setStoreUserMessages } =
		useStoreUserMessagesPreference({ assistanceServiceParams });

	return (
		<ChatHeaderMoreMenuContent
			{...props}
			assistanceServiceParams={assistanceServiceParams}
			storeUserMessages={storeUserMessages}
			storeUserMessagesEligible={storeUserMessagesEligible}
			setStoreUserMessages={setStoreUserMessages}
		/>
	);
};

const ChatHeaderMoreMenuWithLocalPreference = (
	props: ChatHeaderMoreMenuProps,
): React.JSX.Element | null => {
	di(useUserPreferences, fg);
	const { preferences, setStoreUserMessages } = useUserPreferences();

	return (
		<ChatHeaderMoreMenuContent
			{...props}
			storeUserMessages={preferences.storeUserMessages}
			storeUserMessagesEligible={fg('ai_mate_show_store_messages')}
			setStoreUserMessages={setStoreUserMessages}
		/>
	);
};

type ChatHeaderMoreMenuContentProps = ChatHeaderMoreMenuProps & {
	storeUserMessages: boolean;
	storeUserMessagesEligible: boolean;
	setStoreUserMessages: (storeUserMessages: boolean) => void;
};

const ChatHeaderMoreMenuContent = ({
	onFeedbackClick,
	onDebugClick,
	onConversationDelete,
	onConversationRename,
	onDesktopAppClick,
	onSlackAppClick,
	onTeamsAppClick,
	onChromeExtensionClick,
	onCliClick,
	onSettingsClick,
	onShareClick,
	isShareDisabled,
	sitePickerRender,
	subMenuItems = [],
	onMenuOpen,
	storeUserMessages,
	storeUserMessagesEligible,
	setStoreUserMessages,
}: ChatHeaderMoreMenuContentProps): React.JSX.Element | null => {
	di(useAnalytics, fg);

	const { formatMessage: fm } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const showStoreMessages = storeUserMessagesEligible;
	const showRovoDesktopAppMenuButton = fg('ai_mate_show_rovo_desktop_app_menu_button');
	const showRovoMobileQrSubMenu =
		fg('ai_mate_show_rovo_mobile_qr_menu_button') && subMenuItems.length > 0;

	const [isOptionsMenuOpen, setIsOptionsMenuOpen] = useState(false);
	const [openSubMenuKey, setOpenSubMenuKey] = useState<OpenSubMenuKey>('list');

	const canShowSitePicker = sitePickerRender !== undefined;

	const shouldShowShareButton =
		typeof onShareClick === 'function' && isExperimentEnabled('rovo_conversation_sharing');

	const canShowOptionsMenu =
		onConversationDelete !== undefined ||
		onConversationRename !== undefined ||
		onFeedbackClick !== undefined ||
		onDebugClick !== undefined ||
		(showRovoDesktopAppMenuButton && onDesktopAppClick !== undefined) ||
		onSlackAppClick !== undefined ||
		onTeamsAppClick !== undefined ||
		showRovoMobileQrSubMenu ||
		onChromeExtensionClick !== undefined ||
		(onCliClick !== undefined && fg('ai_mate_show_rovo_cli_menu_button')) ||
		onSettingsClick !== undefined ||
		shouldShowShareButton ||
		canShowSitePicker;

	const handleToggleOptionsMenu = useCallback(() => {
		const willBeOpen = !isOptionsMenuOpen;
		setIsOptionsMenuOpen(willBeOpen);
		// Fire onMenuOpen callback when menu is opening (button is clicked)
		if (willBeOpen && onMenuOpen) {
			onMenuOpen();
		}
	}, [isOptionsMenuOpen, setIsOptionsMenuOpen, onMenuOpen]);

	const handleClose = useCallback(() => {
		setOpenSubMenuKey('list');
		handleToggleOptionsMenu();
	}, [handleToggleOptionsMenu]);

	const shouldShowFeedbackButton = typeof onFeedbackClick === 'function';
	const shouldShowDebugButton = typeof onDebugClick === 'function';
	const shouldShowDeleteConversationButton = typeof onConversationDelete === 'function';
	const shouldShowRenameConversationButton = typeof onConversationRename === 'function';
	const shouldShowDesktopAppButton =
		showRovoDesktopAppMenuButton && typeof onDesktopAppClick === 'function';
	const shouldShowSlackAppButton = typeof onSlackAppClick === 'function';
	const shouldShowTeamsAppButton = typeof onTeamsAppClick === 'function';
	const shouldShowChromeExtensionButton = typeof onChromeExtensionClick === 'function';
	const shouldShowCliButton =
		typeof onCliClick === 'function' && fg('ai_mate_show_rovo_cli_menu_button');
	const shouldShowSettingsButton = typeof onSettingsClick === 'function';
	const isShareButtonDisabled = Boolean(isShareDisabled);
	const shareDisabledTooltip = isShareDisabled
		? getShareButtonLabel(isShareDisabled, fm)
		: undefined;

	if (!canShowOptionsMenu) {
		return null;
	}

	const sections = [
		{
			items: canShowSitePicker
				? [
						<SelectorMenuItem
							testId={chatHeaderTestIds.sitePickerMenuItem}
							label={fm(messages.chatHeaderSwitchSiteButtonText)}
							selectComponent={sitePickerRender}
						/>,
					]
				: [],
		},
		{
			items: [
				...(shouldShowRenameConversationButton
					? [
							<MenuItem
								testId={chatHeaderTestIds.renameButton}
								label={fm(messages.chatHeaderRenameButtonText)}
								icon={<EditIcon label="" color={token('color.icon.subtle')} />}
								onClick={() => {
									onConversationRename?.();
									handleToggleOptionsMenu();
								}}
							/>,
						]
					: []),
				...(shouldShowDeleteConversationButton
					? [
							<MenuItem
								testId={chatHeaderTestIds.deleteButton}
								label={fm(messages.chatHeaderDeleteButtonText)}
								labelColor={'color.text.danger'}
								icon={<DeleteIcon label="" color={token('color.icon.danger')} />}
								onClick={() => {
									onConversationDelete?.();
									handleToggleOptionsMenu();
								}}
							/>,
						]
					: []),
			],
		},
		{
			items: [
				...(shouldShowSettingsButton
					? [
							<MenuItem
								testId={chatHeaderTestIds.settingsButton}
								label={fm(messages.chatHeaderSettingsButtonText)}
								icon={<SettingsIcon label="" color={token('color.icon.subtle')} />}
								onClick={() => {
									sendAnalyticsEvent({
										action: 'clicked',
										actionSubject: 'button',
										actionSubjectId: 'settingsAndMemoryButton',
									});
									onSettingsClick?.();
									handleToggleOptionsMenu();
								}}
							/>,
						]
					: []),
				...(shouldShowShareButton
					? [
							<MenuItem
								testId={chatHeaderTestIds.shareButton}
								label={fm(messages.chatHeaderShareButtonText)}
								icon={<UploadIcon label="" color={token('color.icon.subtle')} />}
								isDisabled={isShareButtonDisabled}
								tooltip={shareDisabledTooltip}
								onClick={() => {
									sendAnalyticsEvent({
										action: 'clicked',
										actionSubject: 'button',
										actionSubjectId: 'shareChatButton',
									});
									onShareClick?.();
									handleToggleOptionsMenu();
								}}
							/>,
						]
					: []),
			],
		},
		{
			items: [
				...(shouldShowDesktopAppButton
					? [
							<MenuItem
								testId={chatHeaderTestIds.desktopAppButton}
								label={fm(messages.chatHeaderDesktopAppButtonText)}
								icon={<ScreenIcon label="" color={token('color.icon.subtle')} />}
								onClick={() => {
									onDesktopAppClick?.();
									handleToggleOptionsMenu();
								}}
								isExternalLink
							/>,
						]
					: []),
				...(shouldShowSlackAppButton
					? [
							<MenuItem
								testId={chatHeaderTestIds.slackAppButton}
								label={fm(messages.chatHeaderSlackAppButtonText)}
								icon={<AiChatIcon label="" color={token('color.icon.subtle')} />}
								onClick={() => {
									onSlackAppClick?.();
									handleToggleOptionsMenu();
								}}
								isExternalLink
							/>,
						]
					: []),
				...(shouldShowTeamsAppButton
					? [
							<MenuItem
								testId={chatHeaderTestIds.teamsAppButton}
								label={fm(messages.chatHeaderTeamsAppButtonText)}
								icon={<AiChatIcon label="" color={token('color.icon.subtle')} />}
								onClick={() => {
									onTeamsAppClick?.();
									handleToggleOptionsMenu();
								}}
								isExternalLink
							/>,
						]
					: []),
				...(showRovoMobileQrSubMenu
					? [
							<RovoMobileQrSubMenuItems
								key="rovo-mobile-qr-submenu"
								subMenuItems={subMenuItems}
								onOpenSubMenu={(key) => setOpenSubMenuKey(key as MoreMenuSubMenuKey)}
							/>,
						]
					: []),
				...(shouldShowChromeExtensionButton
					? [
							<MenuItem
								testId={chatHeaderTestIds.chromeExtensionButton}
								label={fm(messages.chatHeaderChromeExtensionButtonText)}
								icon={<BrowserExtensionIcon label="" color={token('color.icon.subtle')} />}
								onClick={() => {
									onChromeExtensionClick?.();
									handleToggleOptionsMenu();
								}}
								isExternalLink
							/>,
						]
					: []),
				...(shouldShowCliButton
					? [
							<MenuItem
								testId={chatHeaderTestIds.cliButton}
								label={fm(messages.chatHeaderCliButtonText)}
								icon={<TerminalIcon label="" color={token('color.icon.subtle')} />}
								onClick={() => {
									onCliClick?.();
									handleToggleOptionsMenu();
								}}
								isExternalLink
							/>,
						]
					: []),
			],
		},
		{
			items: [
				...(fg('ai_mate_show_whats_new_menu_item')
					? [<WhatsNewMenuItem onToggleOptionsMenu={handleToggleOptionsMenu} />]
					: []),
				...(shouldShowFeedbackButton
					? [
							<MenuItem
								testId={chatHeaderTestIds.feedbackButton}
								label={fm(messages.chatHeaderFeedbackButtonText)}
								icon={<FeedbackIcon label="" color={token('color.icon.subtle')} />}
								onClick={() => {
									onFeedbackClick?.();
									handleToggleOptionsMenu();
								}}
							/>,
						]
					: []),
				<MenuItem
					testId={chatHeaderTestIds.getHelpButton}
					label={fm(messages.chatHeaderGetHelpButtonText)}
					icon={<QuestionCircleIcon label="" color={token('color.icon.subtle')} />}
					onClick={() => {
						window.open(
							'https://support.atlassian.com/rovo/docs/using-rovo/',
							'_blank',
							'noopener, noreferrer',
						);
						handleToggleOptionsMenu();
					}}
					isExternalLink={true}
				/>,
			],
		},
		...(shouldShowDebugButton || showStoreMessages
			? [
					{
						items: [
							...(shouldShowDebugButton
								? [
										<MenuItem
											testId={chatHeaderTestIds.debugButton}
											label={fm(messages.chatHeaderDebugButtonText)}
											icon={<DebugIcon label="" color={token('color.icon.subtle')} />}
											onClick={() => {
												onDebugClick?.();
												handleToggleOptionsMenu();
											}}
										/>,
									]
								: []),
							...(showStoreMessages
								? [
										<CheckboxMenuItem
											key="store-messages"
											name="storeUserMessages"
											isChecked={storeUserMessages}
											label={fm(messages.chatStoreUserMessagesLabel)}
											lozengeText="INTERNAL ONLY"
											testId={chatHeaderTestIds.storeMessagesCheckbox}
											onChange={(enabled) => {
												setStoreUserMessages(enabled);
												sendAnalyticsEvent({
													action: 'clicked',
													actionSubject: 'checkbox',
													actionSubjectId: 'storeUserMessages',
													attributes: {
														checked: enabled,
													},
												});
											}}
										/>,
									]
								: []),
						],
					},
				]
			: []),
	];

	const listWidth = showStoreMessages
		? LIST_WIDTH_PX.withStoreMessages
		: LIST_WIDTH_PX.withoutStoreMessages;

	const menuContent = showRovoMobileQrSubMenu ? (
		<ChatHeaderMenuSlidingPanel
			showStoreMessages={showStoreMessages}
			openSubMenuKey={openSubMenuKey}
			subMenuItems={subMenuItems}
			sections={sections}
			onBackClick={() => setOpenSubMenuKey('list')}
			backButtonLabel={fm(messages.chatHeaderMoreMenuBackButtonLabel)}
		/>
	) : (
		<ChatHeaderMenuList sections={sections} />
	);

	return (
		<div
			css={styles.moreMenuPostOfficeSpotlightWrapper}
			data-testid={chatHeaderTestIds.moreMenuPostOfficeSpotlightWrapper}
		>
			<ChatHeaderMenu
				testId={chatHeaderTestIds.moreMenuPopup}
				sections={sections}
				isOpen={isOptionsMenuOpen}
				onClose={showRovoMobileQrSubMenu ? handleClose : handleToggleOptionsMenu}
				placement="bottom-end"
				width={listWidth}
				maxWidth={listWidth}
				content={menuContent}
				trigger={(triggerProps) => (
					<IconButton
						{...triggerProps}
						isSelected={isOptionsMenuOpen}
						testId={chatHeaderTestIds.moreMenuButton}
						appearance="subtle"
						isTooltipDisabled={false}
						label={fm(messages.chatHeaderMoreMenuButtonLabel)}
						icon={MoreIcon}
						onClick={handleToggleOptionsMenu}
					/>
				)}
			/>
		</div>
	);
};
